// A ‘.tsx’ file enables JSX support in the TypeScript compiler, 
// for more information see the following page on the TypeScript wiki:
// https://github.com/Microsoft/TypeScript/wiki/JSX

import React from 'react';
import { TabComponent, TreeViewComponent } from '@syncfusion/ej2-react-navigations';
import FacilityStore from './../stores/FacilityStore';
import FacilityConstants from './../constants/FacilityConstants';
import * as Inovx from '@shared';
import FacilityActions from '../actions/FacilityActions';
import XMLEditor from './XMLEditor';
import OperationProgressDialog from './OperationProgressDialog';

var SystemActions = Inovx.Shared.System.Actions;
var SystemStore = Inovx.Shared.System.Store;

var InovxControls = Inovx.Shared.Controls;
var PermissionService = Inovx.Shared.Permission.PermissionService;
var UrlUtilities = Inovx.Shared.Common.UrlUtilities;
var ListHelper = Inovx.Shared.Common.ListHelper;

declare var InovxLoader: any;

const facilityImg = `${UrlUtilities.getBaseApplicationUrl()}/images/uploadIcon.png`;
interface FacilityFormProps {
    onAfterFacilityDelete?: () => void;
}

interface FacilityFormState {
    isActionInProgress?: boolean;
    actionInProgressMsg?: string;
    isAdding?: boolean;
    isEditing?: boolean;
    facility?: IFacility | any;
    validation?: IValidationDictionaryObject;
    selectedTab?: string;     
    allSchemas: ISchema[];
    libraryTaxonomy: ILibraryTaxonomyDetail[];
    facilityAdapters?: any;
    selectedAdapter?: any;
    editXMLData?: IFacilityAdapterConfiguration;
    isXMLEditorVisible?: boolean;
    isFormSubmitted?: boolean;
    tabsError?: IFacilityTabsError;
    hasChanges?: boolean;
    isDeleteFacilityInProgress?: boolean;
}

function getIsActionInProgress(): boolean {
    return SystemStore.isActionInProgress(FacilityConstants.FACILITY_ADD) ||
        SystemStore.isActionInProgress(FacilityConstants.FACILITY_EDIT) ||
        SystemStore.isActionInProgress(FacilityConstants.FACILITY_DELETE);
}

var _actionInProgressMsg: string
var MANAGE_FACILITY_PERMISSION = "Facility.Manage";
function getFormState(): FacilityFormState {

    // We need to get a copy of the user object here as the store does modifications
    // to the ID arrays directly and that would make the detection of any state 
    /// changes impossible.

    return {
        isAdding: FacilityStore.getIsAdding(),
        isEditing: FacilityStore.getIsEditing(),

        isActionInProgress: getIsActionInProgress(),
        actionInProgressMsg: _actionInProgressMsg,
        allSchemas: FacilityStore.getSchemas(),
        libraryTaxonomy: FacilityStore.getLibraryTaxonomyDetail(),
        facility: FacilityStore.getFacility(),
        validation: FacilityStore.getValidation(FacilityConstants.FACILITY_ADD_FORM),
        selectedTab: FacilityStore.getSelectedTab(),
        facilityAdapters: FacilityStore.getFacilityAdapters(),
        editXMLData: FacilityStore.getXMLEditorData(),
        isXMLEditorVisible: FacilityStore.getXMLEditorOpen(),
        isFormSubmitted: FacilityStore.getIsFormSubmitted(),
        tabsError: FacilityStore.getTabsError(),
        hasChanges: FacilityStore.getHasChanges()
    };
}


class FacilityForm extends React.Component<FacilityFormProps, FacilityFormState> {
    fileInputRef: React.RefObject<HTMLInputElement>;
    previewImage: string = '';
    tabInstance: TabComponent | any;
    isChangeTaxonomyConfirmationShown: boolean = false;
    isChangeSchemaConfirmationShown: boolean = false;
    private treeRef: TreeViewComponent | null = null;
    private subSchemaChangeTimeout: number | null = null;
    constructor(props: FacilityFormProps) {
        super(props);
        this.state = getFormState();
        _actionInProgressMsg = InovxLoader.t("facility:form.saving");
        this.fileInputRef = React.createRef();
    }

    private _saveFacility = () => {
        if (this.state.isAdding) {
            FacilityActions.createFacility();
        } else {
            FacilityActions.editFacility();
        }
    }

    private _deleteFacility = () => {
        SystemActions.displayConfirmationMessage({
            message: InovxLoader.t('facility:message.confirmDeleteFacility'),
            confirmCallback: async () => {
                this.setState({ isDeleteFacilityInProgress: true });
                try {
                    await FacilityActions.deleteFacility(this.state.facility);
                } finally {
                    this.setState({ isDeleteFacilityInProgress: false });
                }
                FacilityActions.openAllFacilities();
            }
        });
    }

    private _synchronizeAdapterData = () => {
        FacilityActions.synchronizeAdapterData(this.state.facility, this.state.selectedAdapter?.AdapterType?.AdapterType);
    }

    private _cancel = async () => {
        await FacilityActions.cancelEdit();
    }
    private _loadState = () => {
        this.setState(getFormState());
    }
    private _onChange = (event: any) => {
        FacilityActions.editFacilityProperty(FacilityConstants.FACILITY_ADD_FORM, event.target.name, event.target.value);
    }
    private _systemChange = () => {
        
        this.setState({ isActionInProgress: getIsActionInProgress() });
    }
    private _checkForUnsavedChanges = (event: any) => {
        if (FacilityStore.getHasChanges()) {
            event.returnValue = InovxLoader.t("common:msgBox.browserSaveChanges");
        };
    }

    componentDidMount() {
        FacilityActions.updateFacilityAdapters([]);
        FacilityStore.addChangeListener(this._loadState);
        SystemStore.addChangeListener(this._systemChange);
        window.addEventListener('beforeunload', this._checkForUnsavedChanges);

        // Initialize SchemaID for new facilities once schemas are available
        //if (this.state.isAdding && !this.state.facility.SchemaID && this.state.allSchemas?.length > 0) {
        //    FacilityActions.editFacilityProperty(FacilityConstants.FACILITY_ADD_FORM, "SchemaID", this.state.allSchemas[0].ID);
        //}
    }

    async componentDidUpdate(prevProps: FacilityFormProps, prevState: FacilityFormState) {
        // If adding and schemas loaded later (or cleared), ensure a default is applied exactly once
        //if (this.state.isAdding && !this.state.facility.SchemaID && this.state.allSchemas?.length > 0 &&
        //    (prevState.allSchemas !== this.state.allSchemas)) {
        //    FacilityActions.editFacilityProperty(FacilityConstants.FACILITY_ADD_FORM, "SchemaID", this.state.allSchemas[0].ID);
        //}

        if (prevState.facility != this.state.facility) {
            this.previewImage = '';
            this.setState({selectedAdapter: null});
        }
    }

    componentWillUnmount() {
        FacilityStore.removeChangeListener(this._loadState);
        SystemStore.removeChangeListener(this._systemChange);
        window.removeEventListener('beforeunload', this._checkForUnsavedChanges);
        if (this.subSchemaChangeTimeout) {
            clearTimeout(this.subSchemaChangeTimeout);
        }
    }

    handleFileInputChange = () => {
        const fileInput = this.fileInputRef.current;
        fileInput.click();
    };
    handleFileChange = (e:any) => {
        const selectedFile = e.target.files[0];
        const reader = new FileReader();

        reader.onload = () => {
            const base64String: any = reader.result;
            this.previewImage = base64String;
            let imgArray = base64String.split(',')
            if (imgArray.length > 0) FacilityActions.editFacilityProperty(FacilityConstants.FACILITY_ADD_FORM, "ThumbnailData", imgArray[1]); 
        };

        if (selectedFile) {
            reader.readAsDataURL(selectedFile);
        }
    };
    private onSchemaChanged = (SchemaID: string) => {
        FacilityActions.editFacilityProperty(FacilityConstants.FACILITY_ADD_FORM, "SchemaID", SchemaID);
    }
    private _onSchemaChanged = (e: any) => {
        let schemaId = e.target.value;
        if (!this.isChangeSchemaConfirmationShown) {
            SystemActions.displayConfirmationMessage({
                message: InovxLoader.t('facility:message.confirmAssignSchemaMessage'),
                confirmCallback: async () => {
                    this.isChangeSchemaConfirmationShown = true;
                    this.onSchemaChanged(schemaId);
                }
            });
        } else {
            this.onSchemaChanged(schemaId);
        }
    }

    private _onSubSchemaChanged = (e: any) => {
        if (!this.treeRef) return;

        if (this.subSchemaChangeTimeout) {
            clearTimeout(this.subSchemaChangeTimeout);
        }

        this.subSchemaChangeTimeout = window.setTimeout(() => {
            const { facility } = this.state;
            const checkedIds = [...this.treeRef.checkedNodes];

            if (facility?.SchemaID) {
                checkedIds.unshift(facility.SchemaID);
            }

            FacilityActions.editFacilityProperty(FacilityConstants.FACILITY_ADD_FORM, "Schemas", checkedIds);
            this.subSchemaChangeTimeout = null;
        }, 0);
    }

    filterSubSchemas = (schemaId: string) => {
        const subs = this.state.allSchemas
            .filter((schema) => schema.IsDependent && schema.Dependencies && schema.Dependencies.includes(schemaId))
            .map((schema) => ({ id: schema.ID, name: schema.Name }));
        return subs || [];
    }

    private onKBTaxonomyChanged = (KBTaxonomyID: string) => {
        FacilityActions.editFacilityProperty(FacilityConstants.FACILITY_ADD_FORM, "KBTaxonomyID", KBTaxonomyID);
    }
    private _onKBTaxonomyChanged = (e: any) => {
        let taxonomyId = e.target.value;
        if (!this.isChangeTaxonomyConfirmationShown) {
            SystemActions.displayConfirmationMessage({
                message: InovxLoader.t('facility:message.confirmAssignTaxonomyMessage'),
                confirmCallback: async () => {
                    this.isChangeTaxonomyConfirmationShown = true;
                    this.onKBTaxonomyChanged(taxonomyId);
                }
            });
        } else {
            this.onKBTaxonomyChanged(taxonomyId);
        }
    }
    getFacilityAdapters = async () => {
        await FacilityActions.getFacilityAdapters(this.state.facility.ID);
    }
    private _onAdaptersChanged = (e: any) => {
        var value = e.target.value;
        let selectedAdapter = this.state.facilityAdapters.find((item: any) => item?.AdapterType?.AdapterType === parseInt(value));
        this.setState({ selectedAdapter: selectedAdapter });
    }
    openXMLEditor = (data: any) => {
        FacilityActions.toggleXMLEditor(true);
        FacilityActions.setXMLEditorData(data);
    }
    changeFormInput = (e:any, index: number) => {
        let selectedAdapter = this.state.selectedAdapter;
        selectedAdapter.AdapterConfigurationItemModels[index]['Value'] = e.target.value;
        this.setState({ selectedAdapter: selectedAdapter })

        let adapters = this.state.facilityAdapters;
        let adapterIndex =  adapters.findIndex((item:any) => item?.AdapterType?.AdapterType === selectedAdapter?.AdapterType?.AdapterType)
        if (adapterIndex > -1) {
            selectedAdapter.FacilityID = this.state.facility.ID;
            adapters[adapterIndex] = selectedAdapter;
        }
        FacilityActions.updateFacilityAdapters(adapters, true)
    }
    onSaveXML = (field: any) => {
        let selectedAdapter = this.state.selectedAdapter;
        let index = selectedAdapter.AdapterConfigurationItemModels.findIndex((item: IFacilityAdapterConfiguration) => item?.Name === field.Name);
        if (index > -1) {
            selectedAdapter.AdapterConfigurationItemModels[index]['Value'] = field.Value;
            this.setState({ selectedAdapter: selectedAdapter });

            let adapters = this.state.facilityAdapters;
            let adapterIndex = adapters.findIndex((item: any) => item?.AdapterType?.AdapterType === selectedAdapter?.AdapterType?.AdapterType)
            if (adapterIndex > -1) {
                adapters[adapterIndex] = selectedAdapter;
            }
            FacilityActions.updateFacilityAdapters(adapters, true)        }
    }

    _getSelectedKBTaxonomyName = () => {
        const { facility, libraryTaxonomy } = this.state;

        if (!facility?.KBTaxonomyID || !Array.isArray(libraryTaxonomy)) {
            return null;
        }

        const match = libraryTaxonomy.find(
            (item: any) => item.ID === facility.KBTaxonomyID
        );

        return match ? match.Name : null;
    }
    _getSelectedSchemaName = () => {
        const { facility, allSchemas } = this.state;

        if (!facility?.SchemaID || !Array.isArray(allSchemas)) {
            return null;
        }

        const match = allSchemas.find(
            (item: any) => item.ID === facility.SchemaID
        );

        return match ? match.Name : null;
    }
    render() {
        var hasManagePermission: boolean = PermissionService.checkPermission(MANAGE_FACILITY_PERMISSION)
        var header = this.state.isAdding ? InovxLoader.t('facility:header.newFacility') : InovxLoader.t('facility:header.editFacility');
        var inputObj = {
            className: 'form-control',
            type: 'text'
        }
        let { facility, selectedTab } = this.state;
        let thumbnail = facilityImg;

        if (this.previewImage) {
            thumbnail = this.previewImage;
        } else if (facility.ThumbnailImageUrl) {
            thumbnail = facility.ThumbnailImageUrl;
        }

        var nameInput = jQuery.extend({
            id: 'facilityApp-name-input',
            name: 'Name',
            value: facility.Name || ''
        }, inputObj);

        var locationInput = jQuery.extend({
            id: 'facilityApp-location-input',
            name: 'Location',
            value: facility.Location || ''
        }, inputObj);

        var synopsisInput = jQuery.extend({
            id: 'facilityApp-synopsis-input',
            name: 'Synopsis',
            value: facility.Synopsis || ''
        }, inputObj);

       
        let headertext: any;
        // Mapping Tab items Header property
        headertext = [{ text: InovxLoader.t('facility:menu.facilityDetails') }, { text: InovxLoader.t('facility:menu.integrations') }, { text: InovxLoader.t('facility:menu.ontology') }];
        var schemaOptions: any[] = [];
        schemaOptions.push(<option key={''} value={''}>{InovxLoader.t('facility:form.selectPrimarySchema')}</option>);
        const primarySchema: ISchema[] = this.state.allSchemas.filter((schema) => !schema.IsDependent);
        for (let i in primarySchema) {
            const schema = primarySchema[i];
            schemaOptions.push(<option key={schema.ID} value={schema.ID}>{schema.Name}</option>);
        }

        //Library Taxonomy
        let taxonomyOptions: any[] = [<option key={''} value={''}>{InovxLoader.t('facility:form.selectTaxonomy')}</option>];
        taxonomyOptions.push(...this.state.libraryTaxonomy
            .filter((item) => item.Type === TaxonomyType.KB)
            .map((taxonomy) => <option key={taxonomy.ID} value={taxonomy.ID}>{taxonomy.Name}</option>));

        const facilityDetailsTab = 
             <div className='tab-card user-form mt-15'>
                <div className="fc-margin">
                    <div className="row d-flex">
                        <div className="col-md-6 field-col">
                            <InovxControls.FormField title={`${InovxLoader.t("facility:form.name")}*`} inputName='Name'>
                                <InovxControls.ValidationInput {...nameInput} className="form-control" onChange={this._onChange} validation={this.state.validation['Name']} readOnly={false} autocomplete="name" />
                            </InovxControls.FormField>
                        </div>
                        <div className="col-md-6 field-col">
                            <InovxControls.FormField title={`${InovxLoader.t("facility:form.location")}`} inputName='Location'>
                                <InovxControls.ValidationInput {...locationInput} className="form-control" onChange={this._onChange} validation={this.state.validation['Location']} readOnly={false} autocomplete="location" />
                            </InovxControls.FormField>
                        </div>
                    </div>

                    <div className="fc-margin">
                        <InovxControls.FormField title={`${InovxLoader.t("facility:form.synopsis")}`} inputName='Synopsis'>
                            <textarea rows={5} {...synopsisInput} onChange={this._onChange} />
                        </InovxControls.FormField>
                    </div>

                    <div className="row d-flex">
                        <div className="col-md-6 field-col">
                            <InovxControls.FormField title={`${InovxLoader.t("facility:form.facilityOrigin")}`} inputName='facilityOrigin'>
                                <input type="text" className="form-control" name='facilityOrigin' readOnly={true} />
                            </InovxControls.FormField>
                        </div>
                        <div className="col-md-6 field-col">
                            <InovxControls.FormField title={`${InovxLoader.t("facility:form.facilityOriginElevation")}`} inputName='facilityOriginElevation'>
                                <input type="text" className="form-control" name='facilityOriginElevation' readOnly={true} />
                            </InovxControls.FormField>
                        </div>
                    </div>

                    <div className="row d-flex">
                        <div className="col-md-6 field-col">
                            <div className="row d-flex">
                                <div className="col-md-12 field-col">
                                    {FacilityStore.getSelectedFacility()?.SchemaID && <InovxControls.FormField title={`${InovxLoader.t("facility:form.primaryFacilitySchema")}`} inputName='Schema'>
                                        <input type="text" className="form-control" name='Schema' value={this._getSelectedSchemaName()} readOnly={true} />
                                    </InovxControls.FormField>}

                                    {!FacilityStore.getSelectedFacility()?.SchemaID && <InovxControls.FormField title={`${InovxLoader.t("facility:form.primaryFacilitySchema")}*`} inputName='Schema' className={`${this.state.validation['SchemaID']?.invalidValue ? 'has-error' : ''}`}>
                                        <select
                                            className="form-control"
                                            value={this.state.facility.SchemaID ?? ''}
                                            onChange={this._onSchemaChanged}
                                        >
                                            {schemaOptions}
                                        </select>
                                    </InovxControls.FormField>}
                                </div>
                            </div>

                            <div className="row d-flex mt-1">
                                <div className="col-md-12 field-col">
                                    {FacilityStore.getSelectedFacility()?.KBTaxonomyID && <InovxControls.FormField title={`${InovxLoader.t("facility:form.knowledgeBookTaxonomy")}`} inputName='KBTaxonomy'>
                                        <input type="text" className="form-control" name='KBTaxonomy' value={this._getSelectedKBTaxonomyName()} readOnly={true} />
                                    </InovxControls.FormField>}

                                    {!FacilityStore.getSelectedFacility()?.KBTaxonomyID && <InovxControls.FormField title={`${InovxLoader.t("facility:form.knowledgeBookTaxonomy")}`} inputName='KBTaxonomy'>
                                        <select
                                            className="form-control"
                                            value={this.state.facility.KBTaxonomyID ?? ''}
                                            onChange={this._onKBTaxonomyChanged}
                                        >
                                            {taxonomyOptions}
                                        </select>
                                    </InovxControls.FormField>}
                                </div>
                            </div>

                            <div className="mt-1">
                                <InovxControls.FormField title={`${InovxLoader.t("facility:form.thumbnail")}`} inputName='Thumbnail'>
                                    <input
                                        type="file"
                                        accept="image/*"
                                        style={{ display: 'none' }}
                                        ref={this.fileInputRef}
                                        onChange={this.handleFileChange}
                                    />
                                    <div className="d-flex j-cc f-dc">
                                        <div onClick={this.handleFileInputChange} className="thumbnail-container cursor-pointer">
                                            <img src={thumbnail} />
                                            <div className="upload-btn">
                                                <img src={`${UrlUtilities.getBaseApplicationUrl()}/images/editIcon.png`} />
                                            </div>
                                        </div>
                                        <div className="d-flex j-cc p-2 text-black">{InovxLoader.t('facility:form.thumbnailRecommendation')}</div>
                                    </div>
                                </InovxControls.FormField>
                            </div>
                        </div>

                        {FacilityStore.getSelectedFacility()?.SchemaID && <div className="col-md-6 field-col">
                            <div className="row d-flex">
                                <div className="col-md-12 field-col">
                                    <InovxControls.FormField title={`${InovxLoader.t("facility:form.facilitySubSchema")}`} inputName='Schemas'>
                                        <div style={{ border: '1px solid #ccc', padding: '10px', height: '200px', width: '100%', overflowY: 'auto' }}>
                                            <TreeViewComponent
                                                className="facility-schema-tree"
                                                ref={(tree) => (this.treeRef = tree)}
                                                fields={{
                                                    dataSource: this.filterSubSchemas(this.state.facility.SchemaID),
                                                    id: 'id',
                                                    text: 'name'
                                                }}
                                                showCheckBox={true}
                                                checkedNodes={this.state.facility.Schemas || []}
                                                nodeChecked={this._onSubSchemaChanged}
                                            />
                                        </div>
                                    </InovxControls.FormField>
                                </div>
                            </div>
                        </div>}
                    </div>

                </div>
            </div>;
        
        const adapters: any = this.state.facilityAdapters?.length > 0 ? this.state.facilityAdapters : [];
        var adaptersOptions: any = [<option disabled key="select" value={''}>{InovxLoader.t("facility:form.pleaseSelectAdapters")}</option >]; 
        for (var i in adapters) {
            adaptersOptions.push(<option key={adapters[i].AdapterType.AdapterType} value={adapters[i].AdapterType.AdapterType}>{adapters[i].AdapterType.AdapterName}</option >);
        }

        const getFormInput = (field: any, key: number) => {
            let fieldName = field.DisplayName.replace(/\s+/g, '_');
            if (field.ConfigurationItemDataType === 1) {
                return <input key={key} type="text" className="form-control" name={fieldName} autoComplete={fieldName} value={field.Value} onChange={(e) => this.changeFormInput(e, key)} />
            } else if (field.ConfigurationItemDataType === 2) {
                return <div className="d-flex w-100" >
                    <input key={key} type="text" readOnly className="form-control" name={fieldName} autoComplete={fieldName} value={field.Value} />
                    <button className="btn btn-primary ml-10" onClick={() => this.openXMLEditor(field)}>{InovxLoader.t("facility:action.editAnnotation")}</button>                   
                </div>
            } else if (field.ConfigurationItemDataType === 3) {
                return <input key={key} type="password" className="form-control" name={fieldName} autoComplete={fieldName} value={field.Value} onChange={(e) => this.changeFormInput(e, key)} />
            }
        }

        const integrationTab =
            <div className='tab-card user-form  mt-15'>
                <div className="fc-margin">
                    
                    <div className="row d-flex">
                        <div className="col-md-6 field-col">
                            <InovxControls.FormField title={`${InovxLoader.t("facility:form.selectAdapterToConfigure")}`} inputName='Adapters'>
                                <select className="form-control" title={InovxLoader.t("facility:form.selectAdapterToConfigure")} value={this.state.selectedAdapter?.AdapterType?.AdapterType || ""} onChange={this._onAdaptersChanged}>
                                    {adaptersOptions}
                                </select>
                                {(this.state.selectedAdapter?.AdapterType?.AdapterType == 17) && <button className="btn btn-primary" onClick={this._synchronizeAdapterData}>{InovxLoader.t('facility:action.synchronize')}</button>}
                            </InovxControls.FormField>
                        </div>
                     </div>
                    

                    {this.state.selectedAdapter && this.state.selectedAdapter.AdapterConfigurationItemModels.map((row: IFacilityAdapterConfiguration, key: number) => {
                        return <div className="row d-flex" key={key}>
                            <div className="col-md-12 field-col">
                                <InovxControls.FormField title={`${row.DisplayName}${row?.IsRequired ? '*' : ''}`} inputName={row.DisplayName} className={`${this.state.isFormSubmitted && row.IsRequired && !row.Value ? 'has-error' : ''}`}>
                                    {getFormInput(row, key) }
                                </InovxControls.FormField>
                            </div>
                        </div>
                    })}
                    
                    {this.state.isXMLEditorVisible && <XMLEditor data={this.state.editXMLData} onSave={this.onSaveXML} />}

                    <input type="password" name="password" autoComplete="password" defaultValue="123" className="hidden-input" />
                    {/* fack input to avoid autocomplete*/ }
                </div>
            </div>;

        return (
            <InovxControls.StaticHeaderForm title={header} saveOnClick={this._saveFacility} saveBtnId="facilityApp-save-btn"
                cancelOnClick={this._cancel} cancelBtnId="facilityApp-cancel-btn"
                deleteOnClick={this.state.isEditing && hasManagePermission ? this._deleteFacility : null} deleteBtnId={this.state.isEditing ? "facilityApp-delete-btn" : null}
                isActionInProgress={this.state.isActionInProgress} inProgressMessage={this.state.actionInProgressMsg}
                saveDisabled={!this.state.hasChanges} cancelDisabled={!this.state.hasChanges}>
                <OperationProgressDialog visible={!!this.state.isDeleteFacilityInProgress} message="Deleting facility." />
                <div>
                    <div className="tab-container">

                        <ul className="nav nav-tabs">
                            <li onClick={(e: any) => FacilityActions.selectTab('details')} className={`${selectedTab === 'details' ? 'active' : ''} ${this.state.tabsError.Details ? 'tab-error' : ''}`}><a data-toggle="tab" href="#tab1">{headertext[0].text}</a></li>
                            {!this.state.isAdding && <>
                            <li onClick={async (e: any) => { FacilityActions.selectTab('integrations'); await this.getFacilityAdapters(); }} className={`${selectedTab === 'integrations' ? 'active' : ''} ${this.state.tabsError.Integrations ? 'tab-error' : ''}`}><a data-toggle="tab" href="#tab4">{headertext[1].text}</a></li></>}
                        </ul>

                        <div className="tab-content">
                            <div id="tab1" className={`tab-pane fade ${selectedTab === 'details' ? 'in active' : ''}`}>
                                <div className="entitlement-permissions">
                                    {facilityDetailsTab}
                                </div>
                            </div>
                            {!this.state.isAdding && <>
                                <div id="tab4" className={`tab-pane fade ${selectedTab === 'integrations' ? 'in active' : ''}`}>
                                    {this.state.selectedTab === 'integrations' && integrationTab}
                                </div>
                            </>}
                        </div>

                    </div>
                </div>
            </InovxControls.StaticHeaderForm>
        );
    }
}

export default FacilityForm;
